import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, Button, Chip, Divider, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { club } from 'apis';
import { useMemo, useRef } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ClubAvatarImage from '../Components/ClubAvatarImage';

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const toValidCoordinatePair = (latValue, lngValue) => {
    const lat = latValue === null || latValue === '' ? NaN : Number(latValue);
    const lng = lngValue === null || lngValue === '' ? NaN : Number(lngValue);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (lat === 0 && lng === 0) return null;

    return { lat, lng };
};

const getGoogleMapsApiKey = () => {
    try {
        const config = window.CONFIG || JSON.parse(window.localStorage.getItem('CONFIG') || '{}');
        return config.VITE_GOOGLE_MAPS_API_KEY || '';
    } catch (error) {
        return '';
    }
};

const getMapOptions = () => ({
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: true
});

const fullscreenButtonStyles = {
    position: 'absolute',
    left: 14,
    bottom: 34,
    zIndex: 20,
    width: 42,
    height: 42,
    background: '#fff',
    color: '#3c4043',
    borderRadius: 1,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.22)',
    '&:hover': {
        background: '#f8f9fa'
    }
};

const getClubLocation = (clubItem, fallback) =>
    [clubItem?.address1, clubItem?.regionName, clubItem?.regionalUnitName].filter(Boolean).join(', ') || fallback;

const getClubDetails = (clubItem, t) =>
    [
        { label: t('Region'), value: clubItem?.regionName },
        { label: t('RegionalUnit'), value: clubItem?.regionalUnitName },
        { label: t('Email'), value: clubItem?.email, href: clubItem?.email ? `mailto:${clubItem.email}` : '' },
        { label: t('URL'), value: clubItem?.url, href: getExternalUrl(clubItem?.url) }
    ].filter((item) => item.value);

const getExternalUrl = (url) => {
    if (!url) return '';
    const trimmedUrl = String(url).trim();

    return /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
};

const SingleClubMapView = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const mapShellRef = useRef(null);

    const clubsQuery = useQuery(
        ['singleClubMap', id],
        async () => {
            const res = await club.getClubsPublic();
            return Array.isArray(res) ? res : [];
        },
        { suspense: false }
    );

    const selectedClub = useMemo(
        () => clubsQuery.data?.find((clubItem) => String(clubItem?.id).toLowerCase() === String(id).toLowerCase()),
        [clubsQuery.data, id]
    );

    const coordinates = useMemo(() => toValidCoordinatePair(selectedClub?.latitude, selectedClub?.longitude), [selectedClub]);

    const exitBrowserFullscreen = () => {
        const documentElement = document;

        if (document.fullscreenElement || documentElement.webkitFullscreenElement) {
            (document.exitFullscreen || documentElement.webkitExitFullscreen)?.call(document);
        }
    };

    const handleToggleFullscreen = () => {
        const mapShell = mapShellRef.current;

        if (document.fullscreenElement || document.webkitFullscreenElement) {
            exitBrowserFullscreen();
            return;
        }

        (mapShell?.requestFullscreen || mapShell?.webkitRequestFullscreen)?.call(mapShell);
    };

    const handleBackToList = () => {
        exitBrowserFullscreen();
        navigate('/public/clubs');
    };

    return (
        <Box sx={{ width: '100%', px: 0, py: 0 }}>
            <Box
                ref={mapShellRef}
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 2000,
                    width: '100vw',
                    height: '100dvh',
                    overflow: 'hidden',
                    background: '#eef3f7'
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToList}
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 20,
                        borderRadius: 1,
                        background: '#fff',
                        color: '#1a73e8',
                        border: '1px solid #d8e6f8',
                        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.16)',
                        textTransform: 'none',
                        fontWeight: 800,
                        '&:hover': { background: '#f5faff' }
                    }}
                >
                    {t('BackToList')}
                </Button>

                {clubsQuery.isLoading && <Skeleton variant="rectangular" width="100%" height="100%" />}

                {!clubsQuery.isLoading && selectedClub && coordinates && (
                    <LoadScript googleMapsApiKey={getGoogleMapsApiKey()} language="el" region="GR">
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={coordinates}
                            zoom={15}
                            options={getMapOptions()}
                        >
                            <Marker position={coordinates} />
                        </GoogleMap>
                    </LoadScript>
                )}

                {!clubsQuery.isLoading && selectedClub && coordinates && (
                    <IconButton aria-label={t('FullscreenMap')} onClick={handleToggleFullscreen} sx={fullscreenButtonStyles}>
                        <FullscreenIcon />
                    </IconButton>
                )}

                {!clubsQuery.isLoading && selectedClub && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: { xs: 72, md: 12 },
                            left: { xs: 12, md: 12 },
                            zIndex: 20,
                            width: { xs: 'calc(100% - 24px)', sm: 360 },
                            maxHeight: 'calc(100% - 24px)',
                            overflowY: 'auto',
                            background: '#fff',
                            borderRadius: 1.5,
                            boxShadow: '0 8px 28px rgba(15, 23, 42, 0.22)',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <Box sx={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: 1.25, p: 1.25 }}>
                            <ClubAvatarImage
                                clubId={selectedClub.id}
                                alt={selectedClub.title || t('UnnamedClub')}
                                iconSize={38}
                                sx={{
                                    height: 90,
                                    width: '100%',
                                    borderRadius: 1,
                                    border: '1px solid #dce4ec'
                                }}
                            />

                            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                                <Typography sx={{ color: '#006ce4', fontWeight: 900, fontSize: 16, lineHeight: 1.25 }}>
                                    {selectedClub.title || t('UnnamedClub')}
                                </Typography>

                                <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ color: '#4b5563' }}>
                                    <LocationOnOutlinedIcon sx={{ fontSize: 17, mt: 0.15, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                        {getClubLocation(selectedClub, t('LocationNotAvailable'))}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    {selectedClub.abbreviation && <Chip size="small" label={selectedClub.abbreviation} sx={{ height: 22, fontWeight: 700 }} />}
                                    {selectedClub.code && <Chip size="small" label={`Code ${selectedClub.code}`} sx={{ height: 22, fontWeight: 700 }} />}
                                </Stack>
                            </Stack>
                        </Box>

                        <Divider />

                        <Stack spacing={1} sx={{ p: 1.25 }}>
                            {getClubDetails(selectedClub, t).map((item) => (
                                <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.label}
                                    </Typography>
                                    {item.href ? (
                                        <Typography
                                            component="a"
                                            href={item.href}
                                            target={item.href.startsWith('mailto:') ? undefined : '_blank'}
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{
                                                fontWeight: 800,
                                                textAlign: 'right',
                                                color: '#075aaa',
                                                textDecoration: 'none',
                                                overflowWrap: 'anywhere',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" sx={{ fontWeight: 800, textAlign: 'right', color: '#111827' }}>
                                            {item.value}
                                        </Typography>
                                    )}
                                </Stack>
                            ))}
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => navigate(`/public/clubs/manage/${selectedClub.id}/details`)}
                                sx={{
                                    mt: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    background: '#0071c2',
                                    '&:hover': { background: '#005fa3' }
                                }}
                            >
                                {t('ViewDetails')}
                            </Button>
                        </Stack>
                    </Box>
                )}

                {!clubsQuery.isLoading && !selectedClub && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 20,
                            p: 4,
                            textAlign: 'center',
                            borderRadius: 2,
                            background: '#fff',
                            boxShadow: '0 8px 28px rgba(15, 23, 42, 0.18)'
                        }}
                    >
                        <Typography variant="h3" sx={{ mb: 1 }}>
                            {t('ClubNotFound')}
                        </Typography>
                        <Typography color="text.secondary">{t('GoBackToListAndChooseAnotherResult')}</Typography>
                    </Box>
                )}

                {!clubsQuery.isLoading && selectedClub && !coordinates && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 20,
                            p: 4,
                            textAlign: 'center',
                            borderRadius: 2,
                            background: '#fff',
                            boxShadow: '0 8px 28px rgba(15, 23, 42, 0.18)'
                        }}
                    >
                        <Typography variant="h3" sx={{ mb: 1 }}>
                            {t('NoMapLocationAvailable')}
                        </Typography>
                        <Typography color="text.secondary">{t('ClubNoValidCoordinates')}</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SingleClubMapView;
